/**
 * Outbound text segmentation — isomorphic, grapheme-safe.
 *
 * Splits a reply into platform-safe chunks. Measuring (limit check) and
 * splitting (boundary choice) are kept separate:
 *  - measure: bytes via TextEncoder, or chars via Unicode code points
 *  - split: ALWAYS at grapheme-cluster boundaries (never breaks an emoji ZWJ
 *    sequence, combining mark, or surrogate pair), preferring higher-level
 *    boundaries (paragraph → line → sentence → word) when they fit.
 *
 * No node-only APIs (TextEncoder + Intl.Segmenter are available in browsers and
 * Node 18+), so the same `measure()` powers the UI length indicator.
 */

import type { TextLimitUnit } from '../channels/channel-text-limits'

/** Appended to the final chunk when a reply is truncated at the chunk cap. */
export const TRUNCATION_MARKER = '…'

/** Fraction of the hard limit reserved as headroom (absorbs unit ambiguity). */
const DEFAULT_HEADROOM = 0.05

export interface SegmentOptions {
  max: number
  unit: TextLimitUnit
  /** Cap on number of chunks; the last is truncated with a marker if exceeded. */
  maxChunks?: number
  /** Override the default 5% headroom (0 = use the full limit; tests use this). */
  headroom?: number
}

export interface SegmentResult {
  chunks: string[]
  /** True when the chunk cap was hit and the last chunk was truncated + marked. */
  truncated: boolean
}

const encoder = new TextEncoder()

/** Size of `text` in the given unit. NOT String.length (UTF-16 code units). */
export function measure(text: string, unit: TextLimitUnit): number {
  return unit === 'bytes' ? encoder.encode(text).length : [...text].length
}

/** Grapheme clusters of `text` with their start indices. */
function graphemes(text: string): Intl.SegmentData[] {
  const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
  return [...seg.segment(text)]
}

/** Largest grapheme-aligned prefix of `text` whose measure is ≤ budget. */
function fitPrefixEnd(text: string, budget: number, unit: TextLimitUnit): number {
  let acc = 0
  let end = 0
  for (const g of graphemes(text)) {
    const size = measure(g.segment, unit)
    if (acc + size > budget) break
    acc += size
    end = g.index + g.segment.length
  }
  // Pathological: even the first grapheme exceeds the budget — include it anyway
  // so the caller always makes progress (avoids an infinite loop).
  if (end === 0 && text.length > 0) {
    const first = graphemes(text)[0]
    end = first.index + first.segment.length
  }
  return end
}

/** Start index of the last sentence inside `text`, or 0 if there's no internal break. */
function lastSentenceBoundary(text: string): number {
  const seg = new Intl.Segmenter(undefined, { granularity: 'sentence' })
  let boundary = 0
  for (const part of seg.segment(text)) {
    if (part.index > 0) boundary = part.index
  }
  return boundary
}

/** Index just after the last whitespace char in `text`, or 0 if none. */
function lastWordBoundary(text: string): number {
  for (let i = text.length - 1; i > 0; i--) {
    if (/\s/.test(text[i])) return i + 1
  }
  return 0
}

/** End index (exclusive) of the first chunk of `text` under `budget`. */
function nextCut(text: string, budget: number, unit: TextLimitUnit): number {
  const hardEnd = fitPrefixEnd(text, budget, unit)
  const prefix = text.slice(0, hardEnd)

  const para = prefix.lastIndexOf('\n\n')
  if (para > 0) return para + 2

  const line = prefix.lastIndexOf('\n')
  if (line > 0) return line + 1

  const sentence = lastSentenceBoundary(prefix)
  if (sentence > 0) return sentence

  const word = lastWordBoundary(prefix)
  if (word > 0) return word

  return hardEnd
}

/** Largest grapheme-aligned prefix of `text` that fits `budget`, then `marker`. */
function truncateWithMarker(
  text: string,
  budget: number,
  unit: TextLimitUnit,
  marker: string,
): string {
  const room = Math.max(0, budget - measure(marker, unit))
  const head = text.slice(0, fitPrefixEnd(text, room, unit)).replace(/\s+$/, '')
  return head + marker
}

/**
 * Split `text` into chunks each within the channel limit (minus headroom).
 * `chunks` is `[]` for empty/whitespace-only input, `[text]` when it already
 * fits. `truncated` is true only when the chunk cap was hit and the last chunk
 * was truncated with a marker (callers should persist what was delivered).
 */
export function segmentText(text: string, opts: SegmentOptions): SegmentResult {
  const { max, unit, maxChunks } = opts
  const headroom = opts.headroom ?? DEFAULT_HEADROOM
  const budget = Math.max(1, Math.floor(max * (1 - headroom)))

  if (text.trim().length === 0) return { chunks: [], truncated: false }
  if (measure(text, unit) <= budget) return { chunks: [text], truncated: false }

  const chunks: string[] = []
  let rest = text
  while (rest.length > 0) {
    if (measure(rest, unit) <= budget) {
      const tail = rest.trim()
      if (tail.length > 0) chunks.push(tail)
      break
    }
    const cut = nextCut(rest, budget, unit)
    const chunk = rest.slice(0, cut).trim()
    if (chunk.length > 0) chunks.push(chunk)
    rest = rest.slice(cut)
  }

  if (maxChunks && maxChunks > 0 && chunks.length > maxChunks) {
    const kept = chunks.slice(0, maxChunks)
    const last = maxChunks - 1
    kept[last] = truncateWithMarker(kept[last] + ' ', budget, unit, TRUNCATION_MARKER)
    return { chunks: kept, truncated: true }
  }

  return { chunks, truncated: false }
}
